import { Button, Card, Grid, IconButton, Paper, Typography } from "@material-ui/core";
import DeleteIcon from '@material-ui/icons/Delete';
import CustomInput from "components/CustomInput";
import NotificationProvider from "components/NotificationProvider";
import { getLotsWithTenantText } from "features/lots/lotUtil";
import { selectTenantName } from "features/user/userSlice";
import { useUpdateUserRestrictionClaimsMutation } from "features/users/usersSlice";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { isAuthorized } from "../../../../../components/Authorize/authorizeUtil";
import { permissionProfiles } from "../../../../../components/Authorize/permissionProfiles";
import ClientAccounts from "./ClientAccounts.js";

const { default: UsersPageStyle } = require("../UsersPageStyle");

const useStyles = UsersPageStyle;

const UserRestrictions = ({ user, clients, lots, userClaims, refetchUsers, userLotRelations, editing = true, handleChange }) => {
    const classes = useStyles();
    const tenantName = useSelector(selectTenantName);

    const [updateRestrictions] = useUpdateUserRestrictionClaimsMutation();

    const [lotClaims, setLotClaims] = useState([]);
    const [accountClaims, setAccountClaims] = useState([]);
    const [hasChanged, setHasChanged] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMessages, setErrorMessages] = useState({});
    const lotIds = lots?.map(x => x.id);
    const accounts = clients?.reduce((acc, x) => acc.concat(x.clientAccounts), []);
    const [selectedClientAccount, setSelectedClientAccount] = useState([]);
    const [selectedDefaultClientAccount, setSelectedDefaultClientAccount] = useState([]);

    const canAddEmptyAccess = isAuthorized(permissionProfiles.USER_MANAGEMENT.ACCESS_LOT_CLIENT_EMPTY_FIELD);

    useEffect(() => {
        let lotIdClaims = userClaims?.filter(c => (c.claimType === 'lotId') && lotIds?.includes(parseInt(c.claimValue)));
        let clientAccountIdClaims = userClaims?.filter(c => (c.claimType === 'clientAccountId'));

        setAccountClaims(clientAccountIdClaims ?? []);
        setLotClaims(lotIdClaims ?? []);

        if (editing) {
            let clientAccounts = [];

            accounts?.forEach(acc => {
                let accountToAdd = [];

                const idFound = clientAccountIdClaims?.find(accId => accId.claimValue === acc.id?.toString());
                if ((acc.isDefault && idFound)) {
                    const accountToPush = clients?.find(c => c.id === acc.clientId)?.clientAccounts ?? [];
                    accountToAdd.push(...accountToPush);
                } else if (idFound) {
                    accountToAdd.push(acc);
                }

                clientAccounts.push(...accountToAdd);
            });

            let defaultClientAccounts = accounts?.filter(acc => acc.isDefault && clientAccounts.find(sacc => sacc.clientId === acc.clientId));
            setSelectedDefaultClientAccount(defaultClientAccounts);
            setSelectedClientAccount(clientAccounts ?? []);

        }
    }, [userClaims]);

    useEffect(() => {
        setErrorMessages({});

        if (handleChange) {
            handleChange({ lotClaims, accountClaims })
        }
    }, [lotClaims, accountClaims]);

    useEffect(() => {
        let clientAccounts = [...selectedClientAccount]
        let defaultClientIds = clientAccounts?.filter(acc => acc.isDefault);

        defaultClientIds?.forEach(deffAcc => {
            const accToRemove = clientAccounts.filter(acc => acc.clientId === deffAcc.clientId && !acc.isDefault);
            clientAccounts = clientAccounts.filter(acc => !accToRemove.includes(acc));
        });

        setAccountClaims(clientAccounts?.map(acc => ({
            userId: user?.id,
            claimType: "clientAccountId",
            claimValue: acc.id.toString()
        })));

        setErrorMessages({});

    }, [selectedClientAccount]);

    const canSaveAccounts = () => {
        for (const acc of selectedDefaultClientAccount) {
            const accounts = selectedClientAccount.filter(sca => acc.clientId === sca.clientId);
            if (accounts.length === 0) {
                return false;
            }
        }

        return true;
    }

    const handleRestrictionsSave = async () => {
        if (!canAddEmptyAccess && (lotClaims.length === 0 || accountClaims?.length === 0)) {
            NotificationProvider.error('You must add at least one lot and one client.');
            return;
        }

        if (!canSaveAccounts()) {
            setErrorMessages({ clientAccounts: 'You must add at least one account for each client' });
            return;
        }

        setSaving(true);

        const objToSend = {
            userId: user?.id,
            lotClaimEntities: lotClaims,
            clientAccountClaimEntities: accountClaims
        };

        let result = await updateRestrictions(objToSend);

        setSaving(false);

        if (!result?.error) {
            NotificationProvider.success('Saved successfully');
            refetchUsers();
        } else {
            NotificationProvider.error('Failed to save');
        }

        setHasChanged(false);
    };

    const lotLookup = lots?.reduce(function (map, obj) {
        map[obj.id] = obj;
        return map;
    }, {});

    const clientLookup = clients?.reduce(function (map, obj) {
        map[obj.id] = obj;
        map.Account = true;
        return map;
    }, {});

    const onAddLot = (lotId) => {
        setHasChanged(true);
        let lotIdList = [...lotClaims];
        let toAdd = {
            userId: user?.id,
            claimType: "lotId",
            claimValue: lotId
        };
        lotIdList.push(toAdd);
        setLotClaims(lotIdList);
    };

    const onAddClient = (clientId) => {
        setHasChanged(true);
        let client = clientLookup[clientId];

        let selectedAccounts = [...selectedClientAccount];
        selectedAccounts.push(...client?.clientAccounts);
        setSelectedClientAccount(selectedAccounts);

        let defaultClientAccount = [...selectedDefaultClientAccount];
        defaultClientAccount.push(client?.clientAccounts.find(acc => acc.isDefault));
        setSelectedDefaultClientAccount(defaultClientAccount);
    };

    const onRemoveLot = (lotId) => {
        setHasChanged(true);
        let lotIdList = lotClaims;
        lotIdList = lotIdList.filter(l => l.claimValue != lotId);
        setLotClaims(lotIdList);
    };

    const onRemoveClient = (clientId) => {
        setHasChanged(true);
        let selectedAccounts = [...selectedClientAccount];
        selectedAccounts = selectedAccounts.filter(acc => acc.clientId !== clientId);
        setSelectedClientAccount(selectedAccounts);

        let defaultClientAccount = [...selectedDefaultClientAccount];
        defaultClientAccount = defaultClientAccount.filter(acc => acc.clientId !== clientId);
        setSelectedDefaultClientAccount(defaultClientAccount);
    };

    const getLotValues = () => {
        return getLotsWithTenantText(lots, tenantName);
    };

    const clientValues = (val) => {
        let values = clients?.filter(c => !selectedDefaultClientAccount?.find(l => l.id === c.clientAccountId) || c.id === val);
        return values;
    };

    return (
        <>
            <Paper className={classes.paper}>
                <div className={classes.label}>Lot Access</div>
                <Grid item className={classes.grids}>
                    <CustomInput
                        id="lot"
                        label="Add Lot Access"
                        value={""}
                        elementType="dropdown"
                        onChange={val => onAddLot(val)}
                        values={getLotValues()}
                        showEmpty
                    />
                </Grid>
                {
                    lotClaims?.map(claim =>
                        <Card className={classes.list}>
                            <Grid item className={classes.restrictions} alignItems='center'>
                                {lotLookup[claim?.claimValue]?.name}
                                <IconButton edge="end" aria-label="delete"
                                    onClick={() => onRemoveLot(claim?.claimValue)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Grid>
                        </Card>
                    )
                }
                <div className={classes.label}>Client Access</div>
                <Grid item className={classes.grids}>
                    <CustomInput
                        id="clientId"
                        label="Add Client Access"
                        value={""}
                        elementType="dropdown"
                        onChange={val => onAddClient(val)}
                        values={clientValues()}
                        showEmpty
                    />
                </Grid>
                <Grid item className={classes.clientAccounts}>{
                    selectedDefaultClientAccount?.map(account =>
                        <ClientAccounts
                            onChangeAccount={() => setHasChanged(true)}
                            client={clientLookup[account?.clientId]}
                            onRemoveClient={onRemoveClient}
                            selectedClientAccount={selectedClientAccount}
                            setSelectedClientAccount={setSelectedClientAccount}
                        />
                    )}
                </Grid>
                {hasChanged && editing &&
                    <Grid container className={classes.wrapperSave}>
                        {errorMessages?.clientAccounts && <Grid item className={classes.alertWrapper}>
                            <Typography variant="body2" color="error" className={classes.saveText}>
                                {errorMessages?.clientAccounts}
                            </Typography>
                        </Grid>}
                        <Grid item>
                            <Button
                                className={classes.resetButton}
                                onClick={handleRestrictionsSave}
                                color="primary"
                                variant="contained"
                                disabled={saving}
                            >
                                {saving ? 'Saving' : 'Save'}
                            </Button>
                        </Grid>
                    </Grid>}
            </Paper>
        </>
    )
}

export default UserRestrictions;
